import React, { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, X } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// AI-powered pre-shift handover briefing.
// Drop-in component: <PreShiftBriefing clientId={x} clientName={y} shiftId={z} />
// Generates a 250-word brief from recent notes, active risks, goals, and meds.
export default function PreShiftBriefing({ clientId, clientName, shiftId, defaultOpen = false }) {
  const [open, setOpen]       = useState(defaultOpen);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data: r } = await api.post('/ai/pre-shift-summary', { clientId, shiftId });
      setData(r);
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not generate briefing');
    } finally {
      setLoading(false);
    }
  };

  if (!clientId) return null;

  // Collapsed pill state
  if (!open && !data) {
    return (
      <button onClick={generate} disabled={loading}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 text-xs font-semibold hover:bg-slate-50 transition-colors disabled:opacity-60"
        title={`Generate AI handover brief for ${clientName || 'this client'}`}>
        {loading ? <Loader2 size={13} className="animate-spin text-violet-500"/> : <Sparkles size={13} className="text-violet-500"/>}
        {loading ? 'Writing the brief…' : 'AI pre-shift brief'}
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-violet-100 ring-1 ring-inset ring-violet-200 flex items-center justify-center">
            <Sparkles size={14} className="text-violet-600"/>
          </div>
          <div>
            <div className="font-semibold text-violet-900 text-sm">Pre-shift brief</div>
            <div className="text-[11px] text-violet-700/80">
              {data?.clientName || clientName} · AI-generated from recent notes & profile
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={generate} disabled={loading} title="Write it again" aria-label="Write the brief again"
            className="p-1.5 rounded-md text-violet-600 hover:bg-violet-100">
            {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
          </button>
          <button onClick={() => setOpen(false)} aria-label="Close the brief" className="p-1.5 rounded-md text-violet-600 hover:bg-violet-100">
            <X size={13}/>
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-6 text-violet-400 text-sm">
          <Loader2 size={18} className="animate-spin mx-auto mb-2"/>
          Generating briefing…
        </div>
      )}

      {data && (
        <div className="space-y-3">
          <div className="text-sm text-violet-900 whitespace-pre-wrap leading-relaxed bg-white/70 rounded-lg p-3 border border-violet-200">
            {data.summary}
          </div>
          {data.contextUsed && (
            <div className="flex flex-wrap gap-1.5 text-[10px]">
              {data.contextUsed.notes > 0 && <Pill label={`${data.contextUsed.notes} recent notes`} />}
              {data.contextUsed.goals > 0 && <Pill label={`${data.contextUsed.goals} active goals`} />}
              {data.contextUsed.activeRisks > 0 && <Pill label={`${data.contextUsed.activeRisks} active risks`} variant="red"/>}
              {data.contextUsed.medications > 0 && <Pill label={`${data.contextUsed.medications} medications`} />}
              {data.contextUsed.incidents > 0 && <Pill label={`${data.contextUsed.incidents} recent incidents`} variant="amber"/>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Pill({ label, variant }) {
  const cls = variant === 'red'
    ? 'bg-red-100 text-red-700 border-red-200'
    : variant === 'amber'
    ? 'bg-amber-100 text-amber-700 border-amber-200'
    : 'bg-white text-violet-700 border-violet-200';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${cls} font-medium`}>{label}</span>;
}
